import type { App } from "../types/platform";
import { APP_STATUS } from "../data/status";
import { LAYER_RADII, posAtAngle } from "../hooks/useActiveNode";
import { StatusMark } from "./StatusMark";

export function AppNode({
  app,
  angle,
  isActive,
  isPinned,
  isConnected,
  onHover,
  onClick
}: {
  app: App;
  angle: number;
  isActive: boolean;
  isPinned: boolean;
  isConnected: boolean;
  onHover: (app: App) => void;
  onClick: (app: App) => void;
}) {
  const p = posAtAngle(angle, LAYER_RADII.app);
  const isInternal = app.category === "internal";
  const labelPrefix = isInternal ? "Internal app" : "Application";
  const size = isInternal ? 60 : 64;

  const bg = isActive
    ? "#fff"
    : isConnected
      ? "rgba(242,129,50,0.10)"
      : "rgba(255,255,255,0.10)";
  const border = isActive
    ? "#F28132"
    : isConnected
      ? "rgba(242,129,50,0.70)"
      : "rgba(255,255,255,0.30)";
  const shadow = isActive
    ? "0 0 40px rgba(242,129,50,0.45)"
    : isConnected
      ? "0 0 24px rgba(242,129,50,0.35)"
      : isInternal
        ? "inset 0 0 0 3px rgba(255,255,255,0.06), inset 0 0 0 4px rgba(255,255,255,0.18)"
        : "none";
  const labelColor = isActive
    ? "#F28132"
    : isConnected
      ? "#F28132"
      : isInternal
        ? "rgba(255,255,255,0.70)"
        : "#fff";
  const status = APP_STATUS[app.status];
  const statusColor = isActive
    ? "#F28132"
    : isConnected
      ? "rgba(242,129,50,0.60)"
      : "rgba(255,255,255,0.35)";

  return (
    <button
      type="button"
      aria-label={`${labelPrefix}: ${app.name}, ${app.tagline} Status: ${status.label}.`}
      aria-pressed={isPinned}
      onMouseEnter={() => onHover(app)}
      onFocus={() => onHover(app)}
      onClick={() => onClick(app)}
      className="absolute outline-none"
      style={{
        left: `${p.x}%`,
        top: `${p.y}%`,
        transform: "translate(-50%, -50%)"
      }}
    >
      <div
        className="relative flex flex-col items-center gap-1.5 transition-transform duration-300"
        style={{ transform: isActive ? "scale(1.12)" : "scale(1)" }}
      >
        <StatusMark
          line={status.line}
          diameter={size + 8}
          color={statusColor}
          className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-colors duration-300"
        />
        <div
          className="flex items-center justify-center rounded-full transition-all duration-300"
          style={{
            width: size,
            height: size,
            backgroundColor: bg,
            border: `1.5px solid ${border}`,
            boxShadow: shadow,
            backdropFilter: "blur(8px)"
          }}
        >
          <div
            className="px-1 text-center text-[10px] tracking-tight transition-colors duration-300"
            style={{
              color: labelColor,
              fontWeight: 600,
              lineHeight: 1.1
            }}
          >
            {app.name}
          </div>
        </div>
      </div>
    </button>
  );
}
